import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { FileText, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsGate = () => {
  const { fetchUser } = useAuth();
  const [sections, setSections] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submitRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/customer/terms`);
        setSections(res.data?.sections || []);
      } catch (e) {
        console.error("Failed to load How it Works:", e);
        toast.error("Could not load How it Works. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allChecked = useMemo(
    () => sections.length > 0 && sections.every((s) => checked[s.id]),
    [sections, checked]
  );

  const handleSubmit = async () => {
    if (!allChecked || submitRef.current) return;
    submitRef.current = true;
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/accept-terms`);
      toast.success("Thank you! Your acceptance has been recorded.");
      await fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
      submitRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-2">
      <div className="flex items-center gap-3 mb-4">
        <FileText size={24} className="text-[#091c3c]" />
        <h1 className="text-2xl font-bold text-[#091c3c] font-['Chivo']">How it Works</h1>
      </div>
      <p className="text-sm text-[#091c3c]/80 mb-4">
        Please read each section below and check its box to confirm. You must confirm every
        section before we can begin calling on your behalf.
      </p>

      <div className="space-y-4">
        {sections.map((s) => (
          <div key={s.id} className="bg-[#091c3c] border border-[#1a3a6a] rounded-xl shadow-lg">
            <div className="p-5 sm:p-6">
              {s.heading && <h2 className="text-lg font-semibold text-white mb-2">{s.heading}</h2>}
              <pre className="whitespace-pre-wrap break-words font-sans text-sm sm:text-[15px] leading-relaxed text-white/90">{s.body}</pre>
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none border-t border-[#1a3a6a] p-4">
              <Checkbox
                checked={!!checked[s.id]}
                onCheckedChange={(v) => setChecked((c) => ({ ...c, [s.id]: v === true }))}
                className="mt-0.5 border-white/40 data-[state=checked]:bg-[#f4cc1e] data-[state=checked]:text-[#091c3c] data-[state=checked]:border-[#f4cc1e]"
              />
              <span className="text-sm text-white">I have read and understand this section.</span>
            </label>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <Button
          onClick={handleSubmit}
          disabled={!allChecked || submitting}
          className="w-full sm:w-auto bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 font-bold uppercase tracking-wide"
        >
          {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
          Accept &amp; Continue
        </Button>
        {!allChecked && <span className="text-xs font-medium text-[#091c3c]/70">Check every section to enable.</span>}
      </div>
    </div>
  );
};

export default TermsGate;
